import { supabase } from "./supabaseClient";
import { getLocalDateString } from "./dateUtils";
import {
  cloneCompletePreferences,
  mapEntryFromDb,
  mapTournamentFromDb,
  toCents,
  toEntryCount,
  toNullableCents,
} from "./supabaseMapping";
import {
  isMissingCompletePreferenceBuyInColumnsError,
  isMissingCompletePreferenceOnboardingColumnError,
  isMissingCommonBaseError,
  isMissingCurrencyColumnError,
  isMissingTicketValueError,
} from "./supabaseSchemaErrors";
import { getPlatformCurrency } from "./currency";

const TOURNAMENT_COLUMNS_WITH_TICKET_AND_CURRENCY =
  "tournament_id,name,buy_in_cents,buy_in_currency,platform,kind,total_players,ticket_value_cents,ticket_value_currency,created_at";
const TOURNAMENT_COLUMNS_WITH_TICKET =
  "tournament_id,name,buy_in_cents,platform,kind,total_players,ticket_value_cents,created_at";
const TOURNAMENT_COLUMNS_WITH_CURRENCY =
  "tournament_id,name,buy_in_cents,buy_in_currency,platform,kind,total_players,created_at";
const TOURNAMENT_LEGACY_COLUMNS =
  "tournament_id,name,buy_in_cents,platform,kind,total_players,created_at";

const isIgnorableTournamentCatalogError = (error, ignoreMissingCatalog) =>
  ignoreMissingCatalog && isMissingCommonBaseError(error);

const selectTournamentCatalog = (table, columns, userId) => {
  let query = supabase.from(table).select(columns);
  if (userId) query = query.eq("user_id", userId);
  return query.order("created_at", { ascending: false });
};

const fetchTournamentCatalog = async ({
  table,
  userId = null,
  ignoreMissingCatalog = false,
}) => {
  const withTicketValue = await selectTournamentCatalog(
    table,
    TOURNAMENT_COLUMNS_WITH_TICKET_AND_CURRENCY,
    userId
  );

  if (
    isIgnorableTournamentCatalogError(
      withTicketValue.error,
      ignoreMissingCatalog
    )
  ) {
    return { data: [], error: null };
  }
  if (
    !isMissingTicketValueError(withTicketValue.error) &&
    !isMissingCurrencyColumnError(withTicketValue.error)
  ) {
    return withTicketValue;
  }

  if (isMissingCurrencyColumnError(withTicketValue.error)) {
    const withoutCurrency = await selectTournamentCatalog(
      table,
      TOURNAMENT_COLUMNS_WITH_TICKET,
      userId
    );

    if (
      isIgnorableTournamentCatalogError(
        withoutCurrency.error,
        ignoreMissingCatalog
      )
    ) {
      return { data: [], error: null };
    }

    if (!isMissingTicketValueError(withoutCurrency.error)) {
      return withoutCurrency;
    }
  }

  const fallback = await selectTournamentCatalog(
    table,
    TOURNAMENT_LEGACY_COLUMNS,
    userId
  );

  if (isIgnorableTournamentCatalogError(fallback.error, ignoreMissingCatalog)) {
    return { data: [], error: null };
  }

  return {
    ...fallback,
    data: (fallback.data || []).map((row) => ({
      ...row,
      ticket_value_cents: 0,
    })),
  };
};

export const fetchTournaments = (userId) =>
  fetchTournamentCatalog({ table: "user_base", userId });

export const fetchCommonTournaments = () =>
  selectTournamentCatalog(
    "common_base",
    TOURNAMENT_COLUMNS_WITH_CURRENCY
  ).then((result) => {
    if (isIgnorableTournamentCatalogError(result.error, true)) {
      return { data: [], error: null };
    }

    if (!isMissingCurrencyColumnError(result.error)) {
      return {
        ...result,
        data: (result.data || []).map((row) => ({
          ...row,
          ticket_value_cents: 0,
          ticket_value_currency: row.buy_in_currency,
        })),
      };
    }

    return selectTournamentCatalog("common_base", TOURNAMENT_LEGACY_COLUMNS).then(
      (fallback) => {
        if (isIgnorableTournamentCatalogError(fallback.error, true)) {
          return { data: [], error: null };
        }

        return {
          ...fallback,
          data: (fallback.data || []).map((row) => ({
            ...row,
            ticket_value_cents: 0,
          })),
        };
      }
    );
  });

export const fetchRegistros = async (userId) => {
  const withCurrency = await supabase
    .from("tournament_entries")
    .select(
      "register_id,user_tournament_id,common_tournament_id,entry_count,played_at,prize_cents,prize_currency,is_sat,ticket_prize_cents,created_at,deleted_at"
    )
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("played_at", { ascending: false })
    .order("created_at", { ascending: false });

  if (!isMissingCurrencyColumnError(withCurrency.error)) return withCurrency;

  return supabase
    .from("tournament_entries")
    .select(
      "register_id,user_tournament_id,common_tournament_id,entry_count,played_at,prize_cents,is_sat,ticket_prize_cents,created_at,deleted_at"
    )
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("played_at", { ascending: false })
    .order("created_at", { ascending: false });
};

export const fetchCompletePreferences = async (userId) => {
  const selectPreferences = (columns) =>
    supabase
      .from("complete_mode_preferences")
      .select(columns)
      .eq("user_id", userId)
      .maybeSingle();

  const selectAttempts = [
    "platforms,buy_in_min_cents,buy_in_max_cents,onboarding_completed_at",
    "platforms,abi_min_cents,abi_max_cents,onboarding_completed_at",
    "platforms,buy_in_min_cents,buy_in_max_cents",
    "platforms,abi_min_cents,abi_max_cents",
  ];

  let lastResult = null;
  for (const columns of selectAttempts) {
    const result = await selectPreferences(columns);
    lastResult = result;

    if (
      isMissingCompletePreferenceBuyInColumnsError(result.error) ||
      isMissingCompletePreferenceOnboardingColumnError(result.error)
    ) {
      continue;
    }

    return result;
  }

  return lastResult;
};

export const saveCompletePreferencesToSupabase = async (userId, preferences) => {
  const normalized = cloneCompletePreferences(preferences);
  const basePayload = {
    user_id: userId,
    platforms: normalized.platforms,
    updated_at: new Date().toISOString(),
  };
  if (normalized.onboardingCompletedAt) {
    basePayload.onboarding_completed_at = normalized.onboardingCompletedAt;
  }
  const buyInMinCents = toNullableCents(normalized.buyInMin);
  const buyInMaxCents = toNullableCents(normalized.buyInMax);
  const savePayload = (payload) =>
    supabase.from("complete_mode_preferences").upsert(payload, {
      onConflict: "user_id",
    });

  const savePayloadWithOnboardingFallback = async (payload) => {
    let result = await savePayload(payload);
    if (
      isMissingCompletePreferenceOnboardingColumnError(result.error) &&
      "onboarding_completed_at" in payload
    ) {
      const {
        onboarding_completed_at: _onboardingCompletedAt,
        ...legacyPayload
      } = payload;
      result = await savePayload(legacyPayload);
    }
    return result;
  };

  let { error } = await savePayloadWithOnboardingFallback({
    ...basePayload,
    buy_in_min_cents: buyInMinCents,
    buy_in_max_cents: buyInMaxCents,
  });

  if (isMissingCompletePreferenceBuyInColumnsError(error)) {
    ({ error } = await savePayloadWithOnboardingFallback({
      ...basePayload,
      abi_min_cents: buyInMinCents,
      abi_max_cents: buyInMaxCents,
    }));
  }

  if (error) throw error;
};

export const saveTournamentToSupabase = async (userId, form, editingId) => {
  const currency = getPlatformCurrency(form.plataforma);
  const payload = {
    user_id: userId,
    name: form.nome.trim(),
    buy_in_cents: toCents(form.buyin),
    buy_in_currency: currency,
    platform: form.plataforma || null,
    kind: form.modalidade || "Vanilla",
    total_players: form.players ? Number(form.players) : null,
    ticket_value_cents:
      form.modalidade === "SAT" ? toCents(form.valorTicket) : 0,
    ticket_value_currency: currency,
  };

  const savePayload = (nextPayload) => editingId
    ? supabase
        .from("user_base")
        .update(nextPayload)
        .eq("user_id", userId)
        .eq("tournament_id", editingId)
        .select()
        .single()
    : supabase.from("user_base").insert(nextPayload).select().single();

  const saveWithFallback = async (nextPayload) => {
    let result = await savePayload(nextPayload);
    if (isMissingCurrencyColumnError(result.error)) {
      const {
        buy_in_currency: _buyInCurrency,
        ticket_value_currency: _ticketValueCurrency,
        ...legacyPayload
      } = nextPayload;
      result = await savePayload(legacyPayload);
    }
    return result;
  };

  let { data, error } = await saveWithFallback(payload);

  if (isMissingTicketValueError(error)) {
    if (payload.ticket_value_cents > 0) {
      throw new Error(
        "O banco ainda nao tem a coluna de valor do ticket SAT. Rode a migracao add-sat-ticket-value-to-user-base antes de salvar este torneio."
      );
    }

    const { ticket_value_cents: _ticketValueCents, ...legacyPayload } = payload;
    ({ data, error } = await saveWithFallback(legacyPayload));
  }

  if (error) throw error;
  return mapTournamentFromDb(data);
};

export const deleteTournamentFromSupabase = async (userId, tournamentId) => {
  const { error } = await supabase
    .from("user_base")
    .delete()
    .eq("user_id", userId)
    .eq("tournament_id", tournamentId);

  if (error) throw error;
};

export const saveRegistroToSupabase = async (userId, form, editingId, tournament) => {
  const isSatTournament = tournament?.modalidade === "SAT";
  const isCommonTournament = tournament?.source === "common";
  const payload = {
    user_id: userId,
    user_tournament_id: isCommonTournament ? null : form.torneioId,
    common_tournament_id: isCommonTournament ? form.torneioId : null,
    entry_count: toEntryCount(form.inscricoes),
    prize_cents: isSatTournament ? null : toCents(form.premiacao),
    prize_currency: getPlatformCurrency(tournament?.plataforma || form.plataforma),
    is_sat: Boolean(form.isSatellite),
    ticket_prize_cents: isSatTournament ? toCents(form.tickets) : null,
  };

  const savePayload = (nextPayload) => editingId
    ? supabase
        .from("tournament_entries")
        .update(nextPayload)
        .eq("user_id", userId)
        .eq("register_id", editingId)
        .select()
        .single()
    : supabase
        .from("tournament_entries")
        .insert({ ...nextPayload, played_at: getLocalDateString() })
        .select()
        .single();

  let { data, error } = await savePayload(payload);
  if (isMissingCurrencyColumnError(error)) {
    const { prize_currency: _prizeCurrency, ...legacyPayload } = payload;
    ({ data, error } = await savePayload(legacyPayload));
  }
  if (error) throw error;
  return mapEntryFromDb(data);
};

export const deleteRegistroFromSupabase = async (userId, registroId) => {
  const { error } = await supabase
    .from("tournament_entries")
    .update({ deleted_at: new Date().toISOString() })
    .eq("user_id", userId)
    .eq("register_id", registroId);

  if (error) throw error;
};
